import {useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Link} from 'react-router-dom'
import {Button} from '@/components/ui/button'
import {Textarea} from '@/components/ui/textarea'
import {Tag} from '@/components/ui/tag'
import {Hint} from '@/components/ui/hint'
import {HelpIcon} from '@/components/help-icon'
import {UploadTrigger} from '@/components/upload-trigger'
import {planAccionHref} from '@/components/accion-ligada-link'
import {CrearMejoraDialog} from '@/components/crear-mejora-dialog'
import {EvidenciaLigada} from '@/components/descargar-evidencia'
import {mintUploadTarget, uploadToStorage, useEvidenceUploadFlow} from '@/hooks/sgsst/evidencias/use-evidence'
import {
  useCorregirEficacia,
  useCreateSeguimiento,
  useVerifyEficacia,
  type EficaciaInput,
  type EficaciaVeredicto,
  type MejoraEditada,
  type SgsstAction,
} from '@/hooks/sgsst/plan/use-actions'
import {publicarExito} from '@/lib/avisos'
import {translateApiError} from '@/lib/errors'
import {formatShortDate} from '@/lib/risk'
import {cn} from '@/lib/utils'

const EFICACIA_TONE: Record<EficaciaVeredicto, 'green' | 'red'> = {
  eficaz: 'green',
  no_eficaz: 'red',
}

/** El veredicto se pinta con su color de semáforo (rampa `.tone-*`), no con el verde de la casa. */
const EFICACIA_PRESSED: Record<EficaciaVeredicto, string> = {
  eficaz: 'bg-accent text-accent-foreground',
  no_eficaz: 'tone-red',
}

const NOTA_MAX = 2000

export function EficaciaTag({veredicto, testId}: {veredicto: EficaciaVeredicto; testId?: string}) {
  const {t} = useTranslation()
  return (
    <Tag tone={EFICACIA_TONE[veredicto]} data-testid={testId}>
      {t(`plan.eficacia.${veredicto}`)}
    </Tag>
  )
}

/** Verificación de eficacia de una acción cumplida (Res. 0312/2019 art. 28, estándar 7.1.2).
 * `editable` sigue habilitado en un año cerrado: la eficacia se sabe meses después del cierre. */
export function ActionEficaciaBlock({
  businessId,
  action,
  editable,
  canOperate,
}: {
  businessId: string
  action: SgsstAction
  editable: boolean
  canOperate: boolean
}) {
  const {t} = useTranslation()
  // La hoja trae la acción de cuando se abrió: se prefiere la fila que devuelve verificar,
  // o el sello nuevo no aparece hasta reabrir.
  const [saved, setSaved] = useState<SgsstAction | null>(null)
  const current = saved ?? action
  const [veredicto, setVeredicto] = useState<EficaciaVeredicto | null>(action.eficacia ?? null)
  const [nota, setNota] = useState(action.eficacia_nota ?? '')
  const [staged, setStaged] = useState<NonNullable<EficaciaInput['soporte']> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [creando, setCreando] = useState(false)
  // El POST del seguimiento devuelve la hija, no el padre: el bloque recuerda su id.
  const [hijaCreadaId, setHijaCreadaId] = useState<string | null>(null)
  const verify = useVerifyEficacia(businessId, action.id)
  const corregir = useCorregirEficacia(businessId, action.id)
  const seguimiento = useCreateSeguimiento(businessId, action.id)

  const upload = useEvidenceUploadFlow(async (file, onProgress) => {
    const target = await mintUploadTarget(businessId, file)
    await uploadToStorage(target, file, onProgress)
    setStaged({
      object_key: target.object_key,
      filename: file.name,
      content_type: file.type,
      size_bytes: file.size,
    })
  })

  // Guardar sin cambiar el veredicto es corregir, no re-atestar: el POST, idempotente por
  // veredicto, descartaría la nota en silencio.
  const corrigiendo = current.eficacia !== null && veredicto === current.eficacia
  const guardando = verify.isPending || corregir.isPending

  function guardar() {
    if (!veredicto) return
    setError(null)
    const onSuccess = (fila: SgsstAction) => {
      setStaged(null)
      setSaved(fila)
    }
    const onError = (err: Error) => setError(translateApiError(err))
    const escrito = {nota: nota.trim() || null, soporte: staged}
    if (corrigiendo) corregir.mutate(escrito, {onSuccess, onError})
    else verify.mutate({veredicto, ...escrito}, {onSuccess, onError})
  }

  async function crearSeguimiento(editado: MejoraEditada) {
    setError(null)
    const hija = await seguimiento.mutateAsync(editado)
    setHijaCreadaId(hija.id)
    publicarExito(t('plan.eficacia.seguimientoCreado'), hija.description, {
      testId: 'seguimiento-creado-banner',
    })
  }

  const sinCambios = veredicto === current.eficacia && staged === null && nota.trim() === (current.eficacia_nota ?? '')

  return (
    <div data-testid="action-eficacia-block">
      <div className="mb-2 flex items-center gap-1.5">
        <h3 className="font-mono text-[10px] font-medium tracking-wider text-muted-foreground uppercase">
          {t('plan.eficacia.title')}
        </h3>
        <HelpIcon label={t('common.help.iconLabelFor', {campo: t('plan.eficacia.title')})} testId="eficacia-tip">
          {t('plan.eficacia.tip')}
        </HelpIcon>
      </div>

      {current.eficacia && (
        <p className="mt-2 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <EficaciaTag veredicto={current.eficacia} testId="eficacia-veredicto" />
          {current.eficacia_verificada_at && (
            <span className="font-mono" data-testid="eficacia-fecha">
              {t('plan.eficacia.verifiedOn', {date: formatShortDate(current.eficacia_verificada_at.slice(0, 10))})}
            </span>
          )}
          {/* La fecha del juicio no se mueve por una corrección: el expediente dice las dos. */}
          {current.eficacia_corregida_at && (
            <span className="font-mono" data-testid="eficacia-corregida">
              {t('plan.eficacia.correctedOn', {date: formatShortDate(current.eficacia_corregida_at.slice(0, 10))})}
            </span>
          )}
          {current.eficacia_soporte && (
            <EvidenciaLigada
              businessId={businessId}
              evidencia={current.eficacia_soporte}
              testId="eficacia-soporte-link"
            />
          )}
        </p>
      )}

      {editable && (
        <div className="mt-3 space-y-2.5">
          <div className="flex w-full gap-0.5 rounded-lg bg-secondary p-0.5" role="group">
            {(['eficaz', 'no_eficaz'] as const).map((opcion) => (
              <button
                key={opcion}
                type="button"
                aria-pressed={veredicto === opcion}
                data-testid={`eficacia-${opcion}`}
                onClick={() => setVeredicto(opcion)}
                className={cn(
                  'flex-1 rounded-md px-2 py-1.5 text-xs whitespace-nowrap transition-colors',
                  veredicto === opcion
                    ? cn('font-semibold shadow-xs', EFICACIA_PRESSED[opcion])
                    : 'text-muted-foreground hover:bg-card hover:text-foreground'
                )}>
                {t(`plan.eficacia.${opcion}Choice`)}
              </button>
            ))}
          </div>
          <Textarea
            value={nota}
            maxLength={NOTA_MAX}
            rows={3}
            data-testid="eficacia-nota"
            placeholder={t('plan.eficacia.notaPlaceholder')}
            onChange={(e) => setNota(e.target.value)}
          />
          {corrigiendo && <Hint testId="eficacia-correccion-aviso">{t('plan.eficacia.correccionHint')}</Hint>}
          <div className="flex flex-wrap items-center gap-2.5">
            <UploadTrigger
              label={staged ? t('plan.eficacia.soporteChange') : t('plan.eficacia.soporteAttach')}
              progress={upload.progress}
              testId="eficacia-soporte"
              onPick={upload.start}
            />
            {staged && (
              <span className="truncate text-xs text-muted-foreground" data-testid="eficacia-soporte-staged">
                {staged.filename}
              </span>
            )}
            <span className="flex-1" />
            <Button
              size="sm"
              disabled={!veredicto || sinCambios || guardando}
              data-testid="eficacia-guardar"
              onClick={guardar}>
              {t(corrigiendo ? 'plan.eficacia.saveCorreccion' : 'plan.eficacia.save')}
            </Button>
          </div>
          {upload.error && (
            <p className="text-xs text-destructive" data-testid="eficacia-soporte-error">
              {upload.error}
            </p>
          )}
        </div>
      )}

      {current.eficacia === 'no_eficaz' && canOperate && (
        <div className="surface-divider mt-3 flex flex-wrap items-center gap-3 border-t pt-3">
          {(current.seguimiento_action_id ?? hijaCreadaId) ? (
            <Link
              to={planAccionHref(businessId, current.seguimiento_action_id ?? hijaCreadaId!)}
              data-testid="ver-seguimiento"
              className="text-xs font-medium text-primary hover:underline">
              {t('plan.eficacia.verSeguimiento')}
            </Link>
          ) : (
            <Button size="sm" variant="outline" data-testid="crear-seguimiento" onClick={() => setCreando(true)}>
              {t('plan.eficacia.seguimientoCta')}
            </Button>
          )}
          <Hint>{t('plan.eficacia.seguimientoHint')}</Hint>
        </div>
      )}

      <CrearMejoraDialog
        businessId={businessId}
        open={creando}
        onOpenChange={setCreando}
        origen={{
          etiqueta: t('mejora.origen.accionPadre'),
          titulo: current.description,
          detalle: current.eficacia_nota ?? undefined,
          testId: 'crear-seguimiento-dialog',
        }}
        descripcion={current.seguimiento_titulo_sugerido}
        responsableId={current.responsible_employee?.id ?? null}
        standardCode={current.standard_code}
        onCrear={crearSeguimiento}
      />

      {error && (
        <p className="mt-2 text-xs text-destructive" data-testid="eficacia-error">
          {error}
        </p>
      )}
    </div>
  )
}
